import { createFileRoute, useNavigate } from '@tanstack/react-router'

import { AppSidebarContainer } from '@/components/app/app-sidebar-container'
import type { OrderMatchingTab } from '@/components/screens/order-matching/order-matching-container'
import {
  OrderMatchingContainer,
  parseOrderMatchingTab,
} from '@/components/screens/order-matching/order-matching-container'
import { ErrorPanel } from '@/components/ui/error-panel'
import { useMe } from '@/providers/me-context'

export const Route = createFileRoute('/t/$tenant_id/order_matching/')({
  // タブを URL に持たせ、再読み込み・共有・戻るで同じタブに戻れるようにする。
  // 絞り込み条件（useFilterBarState のクエリ。キーは steelOrdersFilter / unlinkedMillsheetsFilter）は検証せず通す。検証は各 parse が行い、
  // ここで落とすと URL の条件が初回描画前に消えるため
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: OrderMatchingTab; steelOrdersFilter?: unknown; unlinkedMillsheetsFilter?: unknown } => ({
    tab: parseOrderMatchingTab(search.tab),
    steelOrdersFilter: search.steelOrdersFilter,
    unlinkedMillsheetsFilter: search.unlinkedMillsheetsFilter,
  }),
  component: Page,
})

// route は機能フラグのガードとレイアウト（サイドバー + メイン領域）だけを組み、
// データ取得・ミューテーション配線は OrderMatchingContainer に委譲する
// （routes/t/$tenant_id/index.tsx の MillsheetHomeContainer と同じ分担）
function Page() {
  const { tenant_id: tenantId } = Route.useParams()
  const me = useMe()
  const { tab } = Route.useSearch()
  const navigate = useNavigate()

  // メニュー非表示でも URL 直打ちで到達できるため、画面側でも機能フラグを確認する
  // （v1 route 末尾の Page ガードと同じ）。
  // v2 のテナントトップは発注照合固定（ダッシュボード未移植）なので、発注照合が無効なテナントは
  // ログイン直後にこの画面へ着地する。サイドバーごと描画して他メニューへ抜けられるようにする
  // （ErrorPanel 単体だと導線ゼロの行き止まりになる）
  if (me.tenant.setting?.orderMatchingEnabled !== true) {
    return (
      <div className="flex min-h-screen">
        <AppSidebarContainer tenantId={tenantId} />
        <main className="flex min-w-0 flex-1 items-center justify-center">
          <ErrorPanel
            layout="section"
            title="発注照合機能が有効になっていません"
            description="ご利用のテナントでは発注照合機能が利用できません。利用をご希望の場合は運営までお問い合わせください。"
          />
        </main>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-background">
      <AppSidebarContainer tenantId={tenantId} />
      <main className="flex-1 overflow-x-hidden">
        <OrderMatchingContainer
          tab={tab ?? 'orders'}
          onChangeTab={(next) => {
            void navigate({
              to: '/t/$tenant_id/order_matching',
              params: { tenant_id: tenantId },
              // タブを切り替えても各タブの絞り込み条件（URL のクエリ）は保つ
              search: (prev) => ({ ...prev, tab: next }),
            })
          }}
          onNavigateToImport={() => {
            void navigate({ to: '/t/$tenant_id/order_matching/import', params: { tenant_id: tenantId } })
          }}
        />
      </main>
    </div>
  )
}
